import React from "react";
import { FlashList } from "@shopify/flash-list";
import { View } from "native-base";
import { Platform } from "react-native";
import type { ResolvedCartItem } from "../Universal/API/Context-API/CartContext";
import { useAPI } from "../Universal/API/API";
import { useNotifications } from "../Notifications/Context/NotificationsContext";
import { useAppContext } from "../Universal/API/Context-API/AppContext";
import { useCart } from "../Universal/API/Context-API/CartContext";
import { usePersistedState } from "../Universal/API/Context-API/hooks/usePersistedState";
import { useUserState } from "../Universal/API/Context-API/hooks/useUserState";
import { INITIAL_ORDER_DRAFT, ORDER_DRAFT_STORAGE_KEY } from "./cartConstants";
import CartCheckoutFooter from "./Components/CartCheckoutFooter";
import CartEmptyState from "./Components/CartEmptyState";
import CartHeader from "./Components/CartHeader";
import CartHistorySection from "./Components/CartHistorySection";
import CartItemCard from "./CartItemCard";
import {
  formatOrderHistoryDate,
  getCartItemImage,
  getCurrentOrderItemPrices,
  getResolvedUnitPrice,
  getSavedOrderItemUnitPrice,
} from "./helpers";
import { openCommercialOfferPrintPage } from "./printCommercialOffer";
import type { OrderDraft, OrderHistoryOrder, SavedOrderItem } from "./cartTypes";

interface CartPageProps {
  navigation: any;
  route: any;
  contentHeight?: number;
}

type ResolvedHistoryCartItem = ResolvedCartItem & {
  savedItem: SavedOrderItem;
};

const mapHistoryOrderToDraft = (order: OrderHistoryOrder): OrderDraft => ({
  orderId: order.order_id || "",
  phone: order.phone || "",
  email: order.email || "",
  fullName: order.full_name || "",
  paymentMethod: order.payment_method || INITIAL_ORDER_DRAFT.paymentMethod,
  promoCode: order.promo_code || "",
  addressCommentInn: order.address_comment_inn || "",
  orderName: order.order_name || "",
});

const normalizeHistoryFlag = (value: unknown) =>
  value === true || value === "1" || value === "true";

const getDownloadFileNameFromDisposition = (contentDisposition: string | null) => {
  if (!contentDisposition) {
    return null;
  }

  const utfMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return decodeURIComponent(utfMatch[1]);
  }

  const plainMatch = contentDisposition.match(/filename\s*=\s*"?([^"]+)"?/i);
  return plainMatch?.[1] ?? null;
};

const downloadBlobOnWeb = (blob: Blob, fileName: string) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, 0);
  return true;
};

const CartPage: React.FC<CartPageProps> = ({ navigation, route, contentHeight }) => {
  const { isSmallVersion, scaleAll, USER, allUnitData, X_API_TOKEN } = useAppContext();
  const { sendFormDataPostRequest, serverUrl } = useAPI();
  const { notifyError, notifyInfo, notifySuccess } = useNotifications();
  const userState = useUserState();
  const {
    resolvedItems,
    missingItemIds,
    uniqueItemsCount,
    totalQuantity,
    subtotal,
    discountedSubtotal,
    historyOrders,
    historyOrdersLoading,
    refreshHistoryOrders,
    incrementItem,
    decrementItem,
    removeItem,
    clearCart,
  } = useCart();

  const [storedDraft, setStoredDraft] = usePersistedState<OrderDraft>({
    key: ORDER_DRAFT_STORAGE_KEY,
    initial: INITIAL_ORDER_DRAFT,
  });
  const [selectedHistoryOrder, setSelectedHistoryOrder] =
    React.useState<OrderHistoryOrder | null>(null);
  const [isSelectedHistoryOrderLoading, setIsSelectedHistoryOrderLoading] = React.useState(false);
  const [historyDraft, setHistoryDraft] = React.useState<OrderDraft>(INITIAL_ORDER_DRAFT);
  const [editableHistoryItems, setEditableHistoryItems] = React.useState<SavedOrderItem[]>([]);

  const orderDraft = storedDraft ?? INITIAL_ORDER_DRAFT;
  const isUserLoggedIn = userState.isUserLoggedIn;
  const showDiscountPrices = userState.isUserLoogedInAndShowDiscountPrices;
  const liveCartTotalPrice = showDiscountPrices ? discountedSubtotal || subtotal : subtotal;
  const horizontalPadding = isSmallVersion ? 14 * scaleAll : 22 * scaleAll;
  const routeParams = route?.params ?? {};
  const requestedHistoryMode = normalizeHistoryFlag(routeParams.history);
  const requestedHistoryOrderId =
    typeof routeParams.orderId === "string" && routeParams.orderId.trim().length > 0
      ? routeParams.orderId.trim()
      : null;
  const isHistoryMode = isUserLoggedIn && requestedHistoryMode;

  const historyResolvedItems = React.useMemo<ResolvedHistoryCartItem[]>(() => {
    if (!allUnitData) return [];

    return editableHistoryItems
      .map((savedItem) => {
        const unit = allUnitData[savedItem.id];

        if (!unit || savedItem.quantity <= 0) {
          return null;
        }

        return {
          id: savedItem.id,
          quantity: savedItem.quantity,
          unit,
          savedItem,
        };
      })
      .filter(Boolean) as ResolvedHistoryCartItem[];
  }, [allUnitData, editableHistoryItems]);

  const historyMissingItemIds = React.useMemo(() => {
    if (!allUnitData) {
      return editableHistoryItems.map((item) => item.id);
    }

    return editableHistoryItems
      .filter((item) => !allUnitData[item.id])
      .map((item) => item.id);
  }, [allUnitData, editableHistoryItems]);

  const historyUniqueItemsCount = editableHistoryItems.length;

  const historyTotalQuantity = React.useMemo(
    () => editableHistoryItems.reduce((sum, item) => sum + item.quantity, 0),
    [editableHistoryItems]
  );

  const historyTotalPrice = React.useMemo(
    () =>
      editableHistoryItems.reduce((sum, item) => {
        return (
          sum +
          getSavedOrderItemUnitPrice({
            item,
            showDiscountPrices,
          }) *
            item.quantity
        );
      }, 0),
    [editableHistoryItems, showDiscountPrices]
  );

  const activeResolvedItems = isHistoryMode ? historyResolvedItems : resolvedItems;
  const activeMissingItemIds = isHistoryMode ? historyMissingItemIds : missingItemIds;
  const activeUniqueItemsCount = isHistoryMode ? historyUniqueItemsCount : uniqueItemsCount;
  const activeTotalQuantity = isHistoryMode ? historyTotalQuantity : totalQuantity;
  const activeTotalPrice = isHistoryMode ? historyTotalPrice : liveCartTotalPrice;
  const activeOrderDraft = isHistoryMode ? historyDraft : orderDraft;
  const hasActiveItems = activeResolvedItems.length > 0;

  const fetchSelectedHistoryOrder = React.useCallback(
    async (orderId: string) => {
      if (!isUserLoggedIn || !orderId) {
        setSelectedHistoryOrder(null);
        setEditableHistoryItems([]);
        setHistoryDraft(INITIAL_ORDER_DRAFT);
        return;
      }

      const formData = new FormData();
      formData.append("order_id", orderId);

      if (USER?.access_token) {
        formData.append("access_token", USER.access_token);
      }

      try {
        setIsSelectedHistoryOrderLoading(true);

        const response = await sendFormDataPostRequest({
          link: "get-order",
          formData,
        });

        if (response?.success && response.order) {
          const nextOrder = response.order as OrderHistoryOrder;
          setSelectedHistoryOrder(nextOrder);
          setEditableHistoryItems(Array.isArray(nextOrder.items) ? nextOrder.items : []);
          setHistoryDraft(mapHistoryOrderToDraft(nextOrder));
          return;
        }

        setSelectedHistoryOrder(null);
        setEditableHistoryItems([]);
      } catch (error) {
        notifyError("История заказов", "Не удалось загрузить заказ");
        setSelectedHistoryOrder(null);
        setEditableHistoryItems([]);
      } finally {
        setIsSelectedHistoryOrderLoading(false);
      }
    },
    [USER?.access_token, isUserLoggedIn, notifyError, sendFormDataPostRequest]
  );

  React.useEffect(() => {
    if (!isHistoryMode) {
      setSelectedHistoryOrder(null);
      setEditableHistoryItems([]);
      setHistoryDraft(INITIAL_ORDER_DRAFT);
      return;
    }

    if (!historyOrders.length || requestedHistoryOrderId) {
      return;
    }

    navigation.setParams({
      history: true,
      orderId: historyOrders[0].order_id,
    });
  }, [historyOrders, isHistoryMode, navigation, requestedHistoryOrderId]);

  React.useEffect(() => {
    if (!isHistoryMode || !requestedHistoryOrderId) {
      return;
    }

    fetchSelectedHistoryOrder(requestedHistoryOrderId);
  }, [fetchSelectedHistoryOrder, isHistoryMode, requestedHistoryOrderId]);

  const handleOpenCatalog = React.useCallback(() => {
    navigation.navigate("Catalog");
  }, [navigation]);

  const handleOpenUnit = React.useCallback(
    (id: string) => {
      navigation.navigate("Unit", {
        idUnit: id,
        fromRoute: "Cart",
      });
    },
    [navigation]
  );

  const handleOpenCurrentCart = React.useCallback(() => {
    navigation.replace("Cart", {});
  }, [navigation]);

  const handleOpenHistoryOrder = React.useCallback(
    (orderId: string) => {
      navigation.replace("Cart", {
        history: true,
        orderId,
      });
    },
    [navigation]
  );

  const updateDraft = React.useCallback(
    <K extends keyof OrderDraft>(field: K, value: OrderDraft[K]) => {
      setStoredDraft((prev) => ({
        ...(prev ?? INITIAL_ORDER_DRAFT),
        [field]: value,
      }));
    },
    [setStoredDraft]
  );

  const updateActiveDraft = React.useCallback(
    <K extends keyof OrderDraft>(field: K, value: OrderDraft[K]) => {
      if (isHistoryMode) {
        setHistoryDraft((prev) => ({
          ...(prev ?? INITIAL_ORDER_DRAFT),
          [field]: value,
        }));
        return;
      }

      updateDraft(field, value);
    },
    [isHistoryMode, updateDraft]
  );

  const changeHistoryItemQuantity = React.useCallback((id: string | number, delta: number) => {
    const normalizedId = String(id);

    setEditableHistoryItems((prev) =>
      prev
        .map((item) => {
          if (item.id !== normalizedId) return item;

          return {
            ...item,
            quantity: Math.max(0, item.quantity + delta),
          };
        })
        .filter((item) => item.quantity > 0)
    );
  }, []);

  const handleHistoryIncrement = React.useCallback(
    (id: string | number) => {
      changeHistoryItemQuantity(id, 1);
    },
    [changeHistoryItemQuantity]
  );

  const handleHistoryDecrement = React.useCallback(
    (id: string | number) => {
      changeHistoryItemQuantity(id, -1);
    },
    [changeHistoryItemQuantity]
  );

  const handleHistoryRemove = React.useCallback((id: string | number) => {
    const normalizedId = String(id);
    setEditableHistoryItems((prev) => prev.filter((item) => item.id !== normalizedId));
  }, []);

  const handleHistoryClear = React.useCallback(() => {
    setEditableHistoryItems([]);
  }, []);

  const buildActiveItemsPayload = React.useCallback(() => {
    return isHistoryMode
      ? editableHistoryItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          current_price: item.current_price,
          current_opt_price: item.current_opt_price,
          calculated_by_price: item.calculated_by_price,
        }))
      : activeResolvedItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          ...getCurrentOrderItemPrices({
            unit: item.unit,
            showDiscountPrices,
          }),
        }));
  }, [activeResolvedItems, editableHistoryItems, isHistoryMode, showDiscountPrices]);

  const buildCommercialOfferItemsPayload = React.useCallback(() => {
    if (isHistoryMode) {
      const resolvedById = new Map(
        historyResolvedItems.map((item) => [item.id, item] as const)
      );

      return editableHistoryItems.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        current_price: item.current_price,
        current_opt_price: item.current_opt_price,
        calculated_by_price: item.calculated_by_price,
        image_url: resolvedById.get(item.id)?.unit
          ? getCartItemImage(resolvedById.get(item.id)!.unit)
          : "",
      }));
    }

    return activeResolvedItems.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      ...getCurrentOrderItemPrices({
        unit: item.unit,
        showDiscountPrices,
      }),
      image_url: getCartItemImage(item.unit),
    }));
  }, [
    activeResolvedItems,
    editableHistoryItems,
    historyResolvedItems,
    isHistoryMode,
    showDiscountPrices,
  ]);

  const persistOrder = React.useCallback(
    async ({ clearAfterSuccess = false }: { clearAfterSuccess?: boolean } = {}) => {
      if (!activeResolvedItems.length) {
        notifyInfo("Заказ", "Корзина пуста");
        return;
      }

      const formData = new FormData();
      const status = clearAfterSuccess ? "submitted" : "draft";

      if (activeOrderDraft.orderId) {
        formData.append("order_id", activeOrderDraft.orderId);
      }

      if (USER?.access_token) {
        formData.append("access_token", USER.access_token);
      }

      const itemsPayload = buildActiveItemsPayload();

      formData.append("phone", activeOrderDraft.phone.trim());
      formData.append("email", activeOrderDraft.email.trim());
      formData.append("full_name", activeOrderDraft.fullName.trim());
      formData.append("payment_method", activeOrderDraft.paymentMethod);
      formData.append("promo_code", activeOrderDraft.promoCode.trim());
      formData.append("order_name", activeOrderDraft.orderName.trim());
      formData.append("address_comment_inn", activeOrderDraft.addressCommentInn.trim());
      formData.append("items", JSON.stringify(itemsPayload));
      formData.append("total_quantity", String(activeTotalQuantity));
      formData.append("total_price", String(activeTotalPrice));
      formData.append("status", status);

      try {
        const response = await sendFormDataPostRequest({
          link: "save-order",
          formData,
        });

        if (response?.success && response?.order_id) {
          const nextOrderId = String(response.order_id);
          const nextStatus = String(response.status || (clearAfterSuccess ? "submitted" : "draft"));

          if (isHistoryMode) {
            setHistoryDraft((prev) => ({
              ...(prev ?? INITIAL_ORDER_DRAFT),
              orderId: nextOrderId,
            }));
          } else {
            updateDraft("orderId", nextOrderId);
          }

          await refreshHistoryOrders();

          if (isHistoryMode) {
            navigation.setParams({
              history: true,
              orderId: nextOrderId,
            });
            await fetchSelectedHistoryOrder(nextOrderId);

            notifySuccess(
              nextStatus === "submitted" ? "Заказ оформлен" : "Заказ сохранён",
              nextStatus === "submitted"
                ? "Ваш заказ успешно сформирован, с вами свяжется менеджер для дальнейшей работы"
                : response.message || `Номер заказа: ${nextOrderId}`
            );
            return;
          }

          if (nextStatus === "submitted") {
            clearCart();
            setStoredDraft(INITIAL_ORDER_DRAFT);
            notifySuccess(
              "Заказ оформлен",
              "Ваш заказ успешно сформирован, с вами свяжется менеджер для дальнейшей работы"
            );
            return;
          }

          notifySuccess(
            "Заказ сохранён",
            response.message || `Номер заказа: ${nextOrderId}`
          );
          return;
        }

        notifyError("Заказ", response?.message || "Не удалось сохранить заказ");
      } catch (error) {
        notifyError("Ошибка", "Не удалось сохранить заказ");
      }
    },
    [
      USER?.access_token,
      activeOrderDraft.addressCommentInn,
      activeOrderDraft.email,
      activeOrderDraft.fullName,
      activeOrderDraft.orderId,
      activeOrderDraft.orderName,
      activeOrderDraft.paymentMethod,
      activeOrderDraft.phone,
      activeOrderDraft.promoCode,
      activeResolvedItems,
      activeTotalPrice,
      activeTotalQuantity,
      clearCart,
      editableHistoryItems,
      fetchSelectedHistoryOrder,
      isHistoryMode,
      navigation,
      notifyError,
      notifyInfo,
      notifySuccess,
      refreshHistoryOrders,
      sendFormDataPostRequest,
      setStoredDraft,
      updateDraft,
      buildActiveItemsPayload,
    ]
  );

  const handleSaveOrder = React.useCallback(async () => {
    await persistOrder({ clearAfterSuccess: false });
  }, [persistOrder]);

  const handleSubmitOrder = React.useCallback(async () => {
    await persistOrder({ clearAfterSuccess: true });
  }, [persistOrder]);

  const handleCreateCommercialOffer = React.useCallback(async () => {
    if (!activeResolvedItems.length) {
      notifyInfo("КП", "Корзина пуста");
      return;
    }

    const formData = new FormData();

    if (activeOrderDraft.orderId) {
      formData.append("order_id", activeOrderDraft.orderId);
    }

    if (USER?.access_token) {
      formData.append("access_token", USER.access_token);
    }

    formData.append("phone", activeOrderDraft.phone.trim());
    formData.append("email", activeOrderDraft.email.trim());
    formData.append("full_name", activeOrderDraft.fullName.trim());
    formData.append("payment_method", activeOrderDraft.paymentMethod);
    formData.append("promo_code", activeOrderDraft.promoCode.trim());
    formData.append("order_name", activeOrderDraft.orderName.trim());
    formData.append("address_comment_inn", activeOrderDraft.addressCommentInn.trim());
    formData.append("items", JSON.stringify(buildCommercialOfferItemsPayload()));
    formData.append("total_quantity", String(activeTotalQuantity));
    formData.append("total_price", String(activeTotalPrice));

    try {
      const response = await fetch(serverUrl + "create-quote", {
        method: "POST",
        body: formData,
        headers: {
          "x-api-key": X_API_TOKEN,
          "access-token": USER?.access_token || "",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("create-quote error response", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        try {
          const parsed = JSON.parse(errorText);
          notifyError("КП", parsed?.message || `Ошибка ${response.status}`);
          return;
        } catch (parseError) {
          notifyError("КП", `Ошибка ${response.status}`);
          return;
        }
      }

      const blob = await response.blob();
      const fileName =
        getDownloadFileNameFromDisposition(response.headers.get("content-disposition")) ||
        `kp-${activeOrderDraft.orderId || "quote"}.xlsx`;

      if (Platform.OS === "web" && downloadBlobOnWeb(blob, fileName)) {
        notifySuccess("КП", "Файл коммерческого предложения сформирован");
        return;
      }

      notifyInfo("КП", "Файл коммерческого предложения сформирован");
    } catch (error) {
      console.error("create-quote request failed", error);
      notifyError("КП", "Не удалось сформировать КП");
    }
  }, [
    USER?.access_token,
    X_API_TOKEN,
    activeOrderDraft.addressCommentInn,
    activeOrderDraft.email,
    activeOrderDraft.fullName,
    activeOrderDraft.orderId,
    activeOrderDraft.orderName,
    activeOrderDraft.paymentMethod,
    activeOrderDraft.phone,
    activeOrderDraft.promoCode,
    activeResolvedItems.length,
    activeTotalPrice,
    activeTotalQuantity,
    buildCommercialOfferItemsPayload,
    notifyError,
    notifyInfo,
    notifySuccess,
    serverUrl,
  ]);

  const handlePrintCommercialOffer = React.useCallback(() => {
    if (!activeResolvedItems.length) {
      notifyInfo("Печать КП", "Корзина пуста");
      return;
    }

    if (Platform.OS !== "web") {
      notifyInfo("Печать КП", "Печать коммерческого предложения доступна в веб-версии");
      return;
    }

    const opened = openCommercialOfferPrintPage({
      items: activeResolvedItems.map((item) => {
        const savedItem = "savedItem" in item ? (item as ResolvedHistoryCartItem).savedItem : null;
        const unitPrice = savedItem
          ? getSavedOrderItemUnitPrice({ item: savedItem, showDiscountPrices })
          : getResolvedUnitPrice({ unit: item.unit, showDiscountPrices });
        const details = [item.unit.full_size, item.unit.color1].filter(Boolean).join(" · ");

        return {
          art: item.unit.art,
          name: item.unit.name,
          details,
          imageUrl: getCartItemImage(item.unit),
          quantity: item.quantity,
          unitPrice,
        };
      }),
      totalPrice: activeTotalPrice,
      totalQuantity: activeTotalQuantity,
      customerName: activeOrderDraft.fullName,
      orderName: activeOrderDraft.orderName,
    });

    if (!opened) {
      notifyError("Печать КП", "Не удалось открыть печатную форму. Проверьте блокировку всплывающих окон.");
    }
  }, [
    activeOrderDraft.fullName,
    activeOrderDraft.orderName,
    activeResolvedItems,
    activeTotalPrice,
    activeTotalQuantity,
    notifyError,
    notifyInfo,
    showDiscountPrices,
  ]);

  const estimatedItemSize = 182 * scaleAll;

  const headerTitle = isHistoryMode ? "Заказ из истории" : "Текущий заказ";
  const headerDescription = isHistoryMode
    ? [selectedHistoryOrder?.status === "submitted" ? "Оформлен" : "Сохранён", formatOrderHistoryDate(selectedHistoryOrder?.updated_at || selectedHistoryOrder?.created_at)]
        .filter(Boolean)
        .join(" • ")
    : undefined;
  const headerOrderId = isHistoryMode
    ? selectedHistoryOrder?.order_id || historyDraft.orderId
    : orderDraft.orderId;

  const renderListHeader = React.useMemo(
    () => (
      <>
        <CartHeader
          isSmallVersion={isSmallVersion}
          scaleAll={scaleAll}
          missingItemsCount={activeMissingItemIds.length}
          title={headerTitle}
          description={headerDescription}
          orderId={headerOrderId}
        />

        {isUserLoggedIn && isSmallVersion ? (
          <CartHistorySection
            scaleAll={scaleAll}
            isSmallVersion={isSmallVersion}
            orders={historyOrders}
            showDiscountPrices={showDiscountPrices}
            selectedOrderId={requestedHistoryOrderId}
            isHistoryMode={isHistoryMode}
            onOpenCurrentCart={handleOpenCurrentCart}
            onOpenHistoryOrder={handleOpenHistoryOrder}
          />
        ) : null}
      </>
    ),
    [
      activeMissingItemIds.length,
      handleOpenCurrentCart,
      handleOpenHistoryOrder,
      headerDescription,
      headerOrderId,
      headerTitle,
      historyOrders,
      isHistoryMode,
      isSmallVersion,
      isUserLoggedIn,
      requestedHistoryOrderId,
      scaleAll,
      showDiscountPrices,
    ]
  );

  const renderListEmpty = React.useMemo(() => {
    if (isHistoryMode) {
      if (historyOrdersLoading || isSelectedHistoryOrderLoading) {
        return (
          <CartEmptyState
            scaleAll={scaleAll}
            title="Загружаем заказ"
            description="Сейчас подтянем сохранённые позиции и данные заказа."
          />
        );
      }

      if (!historyOrders.length) {
        return (
          <CartEmptyState
            scaleAll={scaleAll}
            title="История заказов пуста"
            description="Сохранённые и оформленные заказы будут появляться здесь."
            onOpenCatalog={handleOpenCatalog}
          />
        );
      }

      if (requestedHistoryOrderId && !selectedHistoryOrder) {
        return (
          <CartEmptyState
            scaleAll={scaleAll}
            title="Заказ не найден"
            description="Не удалось найти этот заказ в истории текущего пользователя."
            onOpenCatalog={handleOpenCatalog}
          />
        );
      }

      return (
        <CartEmptyState
          scaleAll={scaleAll}
          title="Позиции не найдены"
          description="Для этого заказа в каталоге сейчас не найдено ни одной доступной позиции."
          onOpenCatalog={handleOpenCatalog}
        />
      );
    }

    return <CartEmptyState scaleAll={scaleAll} onOpenCatalog={handleOpenCatalog} />;
  }, [
    handleOpenCatalog,
    historyOrders.length,
    isHistoryMode,
    historyOrdersLoading,
    isSelectedHistoryOrderLoading,
    scaleAll,
  ]);

  const renderListFooter = React.useMemo(() => {
    if (!hasActiveItems) {
      return <View style={{ height: 24 * scaleAll }} />;
    }

    return (
      <CartCheckoutFooter
        isSmallVersion={isSmallVersion}
        scaleAll={scaleAll}
        isUserLoggedIn={isUserLoggedIn}
        orderDraft={activeOrderDraft}
        uniqueItemsCount={activeUniqueItemsCount}
        totalQuantity={activeTotalQuantity}
        totalPrice={activeTotalPrice}
        onUpdateDraft={updateActiveDraft}
        onClearCart={isHistoryMode ? handleHistoryClear : clearCart}
        onOpenCatalog={handleOpenCatalog}
        onCreateCommercialOffer={handleCreateCommercialOffer}
        onPrintCommercialOffer={handlePrintCommercialOffer}
        onSaveOrder={handleSaveOrder}
        onSubmitOrder={handleSubmitOrder}
      />
    );
  }, [
    activeOrderDraft,
    activeTotalPrice,
    activeTotalQuantity,
    activeUniqueItemsCount,
    clearCart,
    handleCreateCommercialOffer,
    handleHistoryClear,
    handleOpenCatalog,
    handleSaveOrder,
    handleSubmitOrder,
    hasActiveItems,
    isHistoryMode,
    isSmallVersion,
    isUserLoggedIn,
    scaleAll,
    updateActiveDraft,
  ]);

  const renderItem = React.useCallback(
    ({
      item,
    }: {
      item: ResolvedCartItem | ResolvedHistoryCartItem;
      index: number;
    }) => {
      const savedItem = "savedItem" in item ? item.savedItem : null;

      return (
        <View
          style={{
            borderRadius: 30 * scaleAll,
            backgroundColor: "#FFFFFF",
            borderWidth: 1,
            borderColor: "#ECE8E1",
            paddingHorizontal: 18 * scaleAll,
            marginBottom: 12 * scaleAll,
          }}
        >
          <CartItemCard
            item={item}
            scaleAll={scaleAll}
            isSmallVersion={isSmallVersion}
            showDiscountPrices={showDiscountPrices}
            onPressItem={handleOpenUnit}
            onIncrement={isHistoryMode ? handleHistoryIncrement : incrementItem}
            onDecrement={isHistoryMode ? handleHistoryDecrement : decrementItem}
            onRemove={isHistoryMode ? handleHistoryRemove : removeItem}
            savedItem={savedItem}
            withTopBorder={false}
          />
        </View>
      );
    },
    [
      decrementItem,
      handleHistoryDecrement,
      handleHistoryIncrement,
      handleHistoryRemove,
      handleOpenUnit,
      incrementItem,
      isHistoryMode,
      isSmallVersion,
      removeItem,
      scaleAll,
      showDiscountPrices,
    ]
  );

  return (
    <View style={{ flex: 1, height: contentHeight ?? "100%", backgroundColor: "#FFFFFF" }}>
      <FlashList
        data={activeResolvedItems}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        estimatedItemSize={estimatedItemSize}
        ListHeaderComponent={renderListHeader}
        ListFooterComponent={renderListFooter}
        ListEmptyComponent={renderListEmpty}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: horizontalPadding,
        }}
      />
    </View>
  );
};

export default React.memo(CartPage);
